import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { supabase } from '../../../lib/supabase';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 News — CMS léger (news_posts). Feature : pages.news.admin.
 * Route : /admin/news + /admin/v2/news. Données manuelles.
 */
const AdminV2News = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, tenantId, agencyId } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', status: 'draft' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: e1 } = await supabase
        .from('news_posts')
        .select('id, title, body, status, published_at, created_at, agency_id')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .limit(1000);
      if (e1) throw e1;
      setRows(data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { error: e1 } = await supabase.from('news_posts').insert({
        tenant_id: tenantId, agency_id: agencyId || null,
        title: form.title, body: form.body, status: form.status,
        published_at: form.status === 'published' ? new Date().toISOString() : null,
        created_by: userProfile?.id || null,
      });
      if (e1) throw e1;
      setShowCreate(false); setForm({ title: '', body: '', status: 'draft' });
      await load();
    } catch (err) { alert(err.message || String(err)); } finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{t('newsV2.title', 'News')}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {rows.length}</p>
            </div>
            <button type="button" onClick={() => setShowCreate((s) => !s)}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">{t('newsV2.create', 'New post')}</button>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          {showCreate && (
            <form onSubmit={submit} className="rounded-2xl bg-card border border-border p-5 space-y-3">
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required
                placeholder={t('newsV2.formTitle', 'Title')}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={4}
                placeholder={t('newsV2.formBody', 'Content')}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary" />
              <div className="flex items-center gap-3 flex-wrap">
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="px-3 py-2 rounded-lg bg-background border border-border text-base">
                  <option value="draft">{t('newsV2.status.draft', 'Draft')}</option>
                  <option value="published">{t('newsV2.status.published', 'Published')}</option>
                </select>
                <button type="submit" disabled={saving} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">
                  {saving ? '…' : t('newsV2.publish', 'Save')}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 rounded-lg border border-border text-sm">{t('common.cancel', 'Cancel')}</button>
              </div>
            </form>
          )}

          {error && <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error">{String(error.message || error)}</div>}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">{t('newsV2.empty', 'No posts yet.')}</div>
          ) : (
            <div className="space-y-3">
              {rows.map((n) => (
                <article key={n.id} className="rounded-2xl bg-card border border-border p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-heading font-semibold text-foreground">{n.title}</h2>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${
                      n.status === 'published' ? 'bg-success/10 text-success' : n.status === 'archived' ? 'bg-muted text-muted-foreground' : 'bg-warning/10 text-warning'
                    }`}>{t(`newsV2.status.${n.status}`, n.status)}</span>
                  </div>
                  {n.body && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line line-clamp-4">{n.body}</p>}
                  <div className="text-[10px] text-muted-foreground/70 mt-2">{new Date(n.created_at).toLocaleString()}</div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2News;
